import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { persistence } from 'services/persistence';
import { loadChatStateTransfer, clearChatStateTransfer } from 'utils/chatStateTransfer';
import { cleanupSearchParam } from 'utils/dom';
import { getPathForPage } from 'utils/navigation';
import { useAsyncEffect } from 'hooks/useAsyncEffectGuard';

const TRANSFER_STORAGE_KEY = 'eir-chat-transfer';

/**
 * Handles chat state transfer from landing page (localStorage) or autoOpen for eir-docs.
 * When autoOpen=true and transfer data exists, stores messages in persistence
 * and navigates to /eir-chat?transfer=true so EirChatPage can pick them up.
 */
export function useChatTransfer(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const isProcessingChatTransferRef = useRef(false);

  useAsyncEffect(async (isCancelled) => {
    const searchParams = new URLSearchParams(location.search);
    const autoOpen = searchParams.get('autoOpen') === 'true';

    if (isProcessingChatTransferRef.current) return;

    if (autoOpen && location.pathname === getPathForPage('eir-docs')) {
      isProcessingChatTransferRef.current = true;
      cleanupSearchParam(searchParams, 'autoOpen', navigate, location.pathname);
      navigate(getPathForPage('eir-chat'));
      isProcessingChatTransferRef.current = false;
      return;
    }

    if (!autoOpen) return;

    const transfer = loadChatStateTransfer();
    if (!transfer) {
      cleanupSearchParam(searchParams, 'autoOpen', navigate, location.pathname);
      return;
    }

    isProcessingChatTransferRef.current = true;
    try {
      // Store transfer data for EirChatPage to pick up
      persistence.setJSON(TRANSFER_STORAGE_KEY, transfer);
      clearChatStateTransfer();

      if (isCancelled()) return;
      cleanupSearchParam(searchParams, 'autoOpen', navigate, location.pathname);

      // Navigate to chat with the initial message as a question (if present)
      if (transfer.message) {
        navigate(`${getPathForPage('eir-chat')}?question=${encodeURIComponent(transfer.message)}`);
      } else {
        navigate(getPathForPage('eir-chat'));
      }
    } catch (error) {
      if (!isCancelled()) {
        console.error('Error loading chat state transfer:', error);
      }
      clearChatStateTransfer();
    } finally {
      isProcessingChatTransferRef.current = false;
    }
  }, [location.search, location.pathname, navigate]);
}
